import { useEffect, useRef } from 'react';

export default function ExcerptSection() {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const els = entry.target.querySelectorAll('.reveal-ex');
            els.forEach((el, i) => {
              setTimeout(() => {
                (el as HTMLElement).style.opacity = '1';
                (el as HTMLElement).style.transform = 'translateY(0)';
              }, i * 150);
            });
          }
        });
      },
      { threshold: 0.1 }
    );
    const el = sectionRef.current;
    if (el) observer.observe(el);
    return () => { if (el) observer.unobserve(el); };
  }, []);

  return (
    <section id="excerpt" className="py-24 md:py-32 bg-stone-950 relative overflow-hidden">
      {/* Background image */}
      <div className="absolute inset-0 opacity-10">
        <img
          src="https://public.readdy.ai/ai/img_res/e8008e35aea0def27c0b76cdc1840036.jpg"
          alt=""
          className="w-full h-full object-cover object-center"
        />
      </div>
      <div className="absolute inset-0 bg-gradient-to-b from-stone-950 via-stone-950/80 to-stone-950"></div>

      <div ref={sectionRef} className="max-w-4xl mx-auto px-4 md:px-10 relative z-10">
        {/* Header */}
        <div
          className="reveal-ex text-center mb-14"
          style={{ opacity: 0, transform: 'translateY(24px)', transition: 'opacity 0.6s ease, transform 0.6s ease' }}
        >
          <span className="inline-block px-4 py-1.5 rounded-full bg-amber-500/10 text-amber-400 text-xs font-semibold mb-4 border border-amber-500/20 tracking-widest uppercase">
            Book Excerpt
          </span>
          <h2 className="text-3xl md:text-5xl font-serif font-bold text-white mb-4">
            A Taste of the{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-300 to-amber-500">
              River
            </span>
          </h2>
          <p className="text-stone-400 text-base">
            Let the words carry you. Read a passage from the book.
          </p>
        </div>

        {/* Excerpt card */}
        <div
          className="reveal-ex relative"
          style={{ opacity: 0, transform: 'translateY(24px)', transition: 'opacity 0.6s ease, transform 0.6s ease' }}
        >
          {/* Large quote mark */}
          <div className="absolute -top-8 -left-4 md:-left-10 text-amber-400/20 font-serif text-[120px] leading-none select-none pointer-events-none">
            &ldquo;
          </div>

          <div className="relative bg-stone-900/80 backdrop-blur-sm border border-stone-700/50 rounded-2xl p-8 md:p-12">
            <div className="space-y-6 text-stone-200 text-lg md:text-xl font-serif leading-relaxed italic">
              <p>
                A river twists, whirls, and bursts across the shore — lavish, unstoppable, alive. In the calm of daylight, it pours again and again, a cascade that mirrors the currents of our own minds.
              </p>
              <p>
                Each drop awakens thought, stirs the heart, and calls us to dive in. This is a river that does more than flow — it transforms.
              </p>
              <p>
                Stand at its edge and you will feel it: the pull of something deeper than the surface, something that has always been moving beneath the noise of ordinary days. The river does not ask permission. It simply goes.
              </p>
              <p>
                And in its going, it teaches us the most essential truth — that we, too, are made to move, to change, to pour ourselves out and be filled again.
              </p>
            </div>

            {/* Attribution */}
            <div className="mt-8 pt-6 border-t border-stone-700/50 flex items-center justify-between flex-wrap gap-4">
              <div>
                <p className="text-amber-300 font-semibold text-sm">— Kevin Hamlett</p>
                <p className="text-stone-500 text-xs mt-0.5">A River PENETRATING THE Soul Line: Book #II</p>
              </div>
              <a
                href="https://www.amazon.com/dp/B0GT29CWSG"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 px-6 py-2.5 rounded-full bg-amber-500 hover:bg-amber-400 text-stone-950 font-bold text-sm transition-all cursor-pointer whitespace-nowrap"
              >
                <i className="ri-book-open-line"></i>
                Read the Full Book
              </a>
            </div>
          </div>

          {/* Closing quote */}
          <div className="absolute -bottom-8 -right-4 md:-right-10 text-amber-400/20 font-serif text-[120px] leading-none select-none pointer-events-none rotate-180">
            &ldquo;
          </div>
        </div>

        {/* Themes pills */}
        <div
          className="reveal-ex mt-16 flex flex-wrap items-center justify-center gap-3"
          style={{ opacity: 0, transform: 'translateY(24px)', transition: 'opacity 0.6s ease, transform 0.6s ease' }}
        >
          {['Consciousness', 'Nature', 'Transformation', 'Soul', 'Meditation', 'Spiritual Growth', 'Inner Peace', 'Lyrical Prose'].map((tag) => (
            <span
              key={tag}
              className="px-4 py-1.5 rounded-full bg-stone-800 border border-stone-700 text-stone-400 text-xs font-medium hover:border-amber-500/40 hover:text-amber-300 transition-all cursor-default"
            >
              {tag}
            </span>
          ))}
        </div>
      </div>
    </section>
  );
}
